// Fatia S1: plano atual, teste grátis, troca de plano, forma de pagamento, faturas e cancelamento.
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Check, ExternalLink, LayoutTemplate } from 'lucide-react'
import type { MeAccount, PlanId } from '@impulso/shared'
import type { BillingType, SubscriptionDto } from '@impulso/shared/api/billing'
import { api, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import { ErrorState, Loading, PageHead, Switch } from '../../ui'
import AccountGate from '../../features/account/AccountGate'
import { ACCOUNT_STATUS_LABEL, dateBR } from '../../features/account/labels'
import { money, planFeatures, priceLabel, usePlans, useSubscription } from '../../features/billing/api'
import { FormError } from '../../features/auth/AuthShell'
import '../../features/account/pages.css'

const PAY_LABEL: Record<BillingType, string> = { PIX: 'Pix', BOLETO: 'Boleto', CREDIT_CARD: 'Cartão de crédito' }
const INVOICE_LABEL = { pending: { t: 'Aguardando', c: 'amber' }, paid: { t: 'Paga', c: 'green' }, overdue: { t: 'Vencida', c: 'red' }, canceled: { t: 'Cancelada', c: '' }, refunded: { t: 'Estornada', c: '' } } as const

export default function Plan() {
  return <AccountGate permission="account.billing">{(account) => <PlanInner account={account} />}</AccountGate>
}

function PlanInner({ account }: { account: MeAccount }) {
  const sub = useSubscription(account.id)
  const plans = usePlans()
  const qc = useQueryClient()
  const { refresh } = useSession()
  const toast = useToast()
  const confirm = useConfirm()
  const [selected, setSelected] = useState<PlanId | null>(null)
  const [billingType, setBillingType] = useState<BillingType>('PIX')
  const [cpfCnpj, setCpfCnpj] = useState('')
  const [extra, setExtra] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (sub.isLoading || plans.isLoading) return <Loading />
  if (sub.error) return <ErrorState error={sub.error} onRetry={() => sub.refetch()} />
  if (!sub.data) return null
  const s = sub.data
  const options = (plans.data ?? []).filter((p) => p.kind === account.kind)
  const target = selected ?? s.plan.id
  const targetPlan = options.find((p) => p.id === target) ?? s.plan
  const extraCompanies = account.kind === 'agency' ? (extra ?? s.extraCompanies) : 0
  const status = ACCOUNT_STATUS_LABEL[s.accountStatus]
  const needsDoc = s.provider === 'asaas' && !s.hasPaymentMethod
  const isCurrent = s.hasPaymentMethod && s.status !== 'canceled' && target === s.plan.id && extraCompanies === s.extraCompanies && billingType === s.billingType
  const included = [s.extraCompanies > 0 ? `${s.extraCompanies} empresa(s) extra` : '', ...s.addons.filter((a) => a.active).map((a) => a.name.toLowerCase())].filter(Boolean)
  const addonsTotal = s.addons.filter((a) => a.active).reduce((sum, a) => sum + a.price, 0)
  const total = targetPlan.price + extraCompanies * (targetPlan.extraCompanyPrice ?? 0) + addonsTotal

  async function done(r: SubscriptionDto, msg: string) {
    qc.setQueryData(['subscription', account.id], r)
    await qc.invalidateQueries({ queryKey: ['account', account.id] })
    await refresh()
    toast(msg)
  }
  async function subscribe() {
    setError(null)
    setBusy(true)
    try {
      const r = await api.post<SubscriptionDto>(`/accounts/${account.id}/subscription`, { planId: target, billingType, ...(needsDoc ? { cpfCnpj } : {}), ...(account.kind === 'agency' ? { extraCompanies } : {}) })
      setSelected(null)
      await done(r, r.status === 'active' ? 'Assinatura ativa. Obrigado!' : 'Assinatura criada. Assim que o pagamento for confirmado, a conta é ativada.')
    } catch (err) { setError(errorMessage(err)) } finally { setBusy(false) }
  }
  async function toggleAddon(id: string, active: boolean) {
    if (!active && !(await confirm({ title: 'Desligar o adicional?', message: 'As páginas publicadas saem do ar até você ligar de novo.', confirmLabel: 'Desligar', danger: true }))) return
    setError(null)
    setBusy(true)
    try {
      const r = await api.put<SubscriptionDto>(`/accounts/${account.id}/subscription/addons/${id}`, { active })
      await done(r, active ? 'Adicional ativado.' : 'Adicional desligado.')
      await qc.invalidateQueries({ queryKey: ['tracking'] })
      await qc.invalidateQueries({ queryKey: ['landing-pages'] })
    } catch (err) { setError(errorMessage(err)) } finally { setBusy(false) }
  }
  async function cancel() {
    if (!(await confirm({ title: 'Cancelar a assinatura?', message: 'Se já estiver paga, você continua usando até o fim do período. Depois a conta fica somente para leitura.', confirmLabel: 'Cancelar assinatura', cancelLabel: 'Manter', danger: true }))) return
    setError(null)
    setBusy(true)
    try { await done(await api.post<SubscriptionDto>(`/accounts/${account.id}/subscription/cancel`), 'Assinatura cancelada.') } catch (err) { setError(errorMessage(err)) } finally { setBusy(false) }
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Plano e cobrança" sub="O valor dos anúncios é pago direto ao Google e à Meta. O plano cobre só o uso do Seu Impulso." />
      <FormError error={error} />

      <div className="card">
        <div className="row wrap between" style={{ gap: 12 }}>
          <div>
            <div className="small muted">Seu plano</div>
            <h2 style={{ fontSize: 22 }}>{s.plan.name}</h2>
            <div className="small muted mt-8">{money(s.monthlyTotal)}/mês{included.length ? ` (inclui ${included.join(' e ')})` : ''}</div>
          </div>
          <span className={`badge ${status.cls}`}>{status.text}</span>
        </div>
        <div className="mt-12 small">
          {s.status === 'trialing' && s.trialEndsAt && <>Teste grátis até <b>{dateBR(s.trialEndsAt)}</b>. Escolha a forma de pagamento abaixo para continuar depois disso.</>}
          {s.status === 'active' && s.currentPeriodEnd && <>Próxima renovação em <b>{dateBR(s.currentPeriodEnd)}</b> · {PAY_LABEL[s.billingType as BillingType] ?? '—'}</>}
          {s.status === 'past_due' && <span style={{ color: 'var(--red)' }}>Pagamento pendente desde {dateBR(s.pastDueSince)}. Depois de 7 dias a conta fica somente para leitura e o piloto é pausado.</span>}
          {s.status === 'canceled' && <>Assinatura cancelada{s.accountStatus === 'active' && s.currentPeriodEnd ? `; você usa até ${dateBR(s.currentPeriodEnd)}` : ''}. Escolha um plano para reativar.</>}
        </div>
        <div className="grid g-2 mt-16" style={{ gap: 12 }}>
          <Usage label={account.kind === 'agency' ? 'Empresas-clientes' : 'Empresas'} used={s.usage.companies} max={s.entitlements.maxCompanies} />
          <Usage label="Usuários" used={s.usage.users} max={s.entitlements.maxUsers} />
        </div>
      </div>

      <div>
        <h2 style={{ marginBottom: 12 }}>{s.hasPaymentMethod && s.status !== 'canceled' ? 'Trocar de plano' : 'Escolha seu plano'}</h2>
        <div className="grid g-3">
          {options.map((p) => {
            const on = p.id === target
            return (
              <button type="button" key={p.id} className={`option-card ${on ? 'on' : ''}`} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }} onClick={() => setSelected(p.id)} disabled={p.price <= 0} aria-pressed={on}>
                <div className="row between plan-head"><h3>{p.name}</h3>{p.id === s.plan.id && <span className="badge purple">Atual</span>}</div>
                <div><span style={{ fontSize: 24, fontWeight: 700 }}>{priceLabel(p)}</span>{p.price > 0 && <span className="muted">/mês</span>}</div>
                <div className="stack" style={{ gap: 4 }}>
                  {planFeatures(p).slice(0, 5).map((f) => <div key={f} className="row small" style={{ alignItems: 'flex-start' }}><Check size={14} color="var(--green)" style={{ flexShrink: 0, marginTop: 3 }} /> {f}</div>)}
                </div>
                {p.price <= 0 && <span className="small muted">Fale com a gente</span>}
              </button>
            )
          })}
        </div>
      </div>

      <div>
        <h2 style={{ marginBottom: 12 }}>Adicionais</h2>
        <div className="grid g-fit">
          {s.addons.map((a) => (
            <div key={a.id} className="card" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', borderColor: a.active ? 'var(--primary)' : undefined }}>
              <span className="ic purple"><LayoutTemplate size={18} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row between" style={{ gap: 8 }}><h3>{a.name}</h3>{a.active && <span className="badge green">Ativo</span>}</div>
                <p className="small muted mt-8">{a.description}</p>
                <div className="mt-8"><b>+ {money(a.price)}</b><span className="muted">/mês</span></div>
                {s.status === 'trialing' && <p className="small muted mt-8">No teste grátis você já pode usar; a cobrança entra junto com a assinatura.</p>}
              </div>
              <Switch on={a.active} disabled={busy} label={`${a.active ? 'Desligar' : 'Ligar'} ${a.name}`} onChange={(v) => void toggleAddon(a.id, v)} />
            </div>
          ))}
        </div>
      </div>

      <div className="card stack" style={{ gap: 14 }}>
        <h2>Forma de pagamento</h2>
        <div className="row wrap" style={{ gap: 8 }} role="radiogroup" aria-label="Forma de pagamento">
          {(Object.keys(PAY_LABEL) as BillingType[]).map((b) => <button type="button" role="radio" aria-checked={billingType === b} key={b} className={`chip ${billingType === b ? 'on' : ''}`} onClick={() => setBillingType(b)}>{PAY_LABEL[b]}</button>)}
        </div>
        {needsDoc && (
          <div className="field" style={{ maxWidth: 320 }}>
            <label htmlFor="pl-doc">CPF ou CNPJ de quem paga</label>
            <input id="pl-doc" className="input" inputMode="numeric" value={cpfCnpj} onChange={(e) => setCpfCnpj(e.target.value)} placeholder="Só números" />
            <span className="small muted">Necessário para emitir a cobrança e a nota fiscal.</span>
          </div>
        )}
        {account.kind === 'agency' && targetPlan.extraCompanyPrice != null && (
          <div className="field" style={{ maxWidth: 320 }}>
            <label htmlFor="pl-extra">Empresas extras ({money(targetPlan.extraCompanyPrice)} cada por mês)</label>
            <input id="pl-extra" className="input" type="number" min={0} max={500} value={extraCompanies} onChange={(e) => setExtra(Math.max(0, Math.floor(+e.target.value || 0)))} />
          </div>
        )}
        <div className="form-actions" style={{ justifyContent: 'space-between' }}>
          <div><span className="muted">Total:</span> <b style={{ fontSize: 18 }}>{money(total)}</b><span className="muted">/mês</span>{s.status === 'trialing' && s.trialEndsAt && new Date(s.trialEndsAt) > new Date() && <span className="small muted"> · primeira cobrança em {dateBR(s.trialEndsAt)}</span>}</div>
          <button className="btn primary lg" disabled={busy || isCurrent || targetPlan.price <= 0 || (needsDoc && cpfCnpj.replace(/\D/g, '').length < 11)} onClick={subscribe}>
            {busy ? 'Processando…' : s.hasPaymentMethod && s.status !== 'canceled' ? 'Salvar mudança' : 'Assinar'}
          </button>
        </div>
      </div>

      <div className="card flush">
        <div style={{ padding: '16px 20px' }}><h2>Faturas</h2></div>
        {s.invoices.length === 0 ? (
          <p className="small muted" style={{ padding: '0 20px 20px' }}>Nenhuma fatura ainda.</p>
        ) : (
          <div className="table-wrap">
            <table className="table stack-sm">
              <thead><tr><th>Vencimento</th><th className="right">Valor</th><th>Situação</th><th /></tr></thead>
              <tbody>
                {s.invoices.map((i) => (
                  <tr key={i.id}>
                    <td data-label="Vencimento">{new Date(`${i.dueDate}T12:00:00`).toLocaleDateString('pt-BR')}</td>
                    <td className="right" data-label="Valor">{money(i.amount)}</td>
                    <td data-label="Situação"><span className={`badge ${INVOICE_LABEL[i.status].c}`}>{INVOICE_LABEL[i.status].t}</span></td>
                    <td className="right" data-label="">{i.url && <a className="btn sm" href={i.url} target="_blank" rel="noreferrer">Ver <ExternalLink size={13} /></a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {s.status !== 'canceled' && (
        <div><button className="btn ghost danger flush-left" disabled={busy} onClick={cancel}>Cancelar assinatura</button></div>
      )}
    </div>
  )
}

function Usage({ label, used, max }: { label: string; used: number; max: number }) {
  const unlimited = max >= 500
  const full = !unlimited && used >= max
  const pct = unlimited ? 5 : Math.min(100, Math.round((used / Math.max(1, max)) * 100))
  return (
    <div>
      <div className="row between small"><span>{label}</span><span className="strong">{used} de {unlimited ? 'ilimitados' : max}</span></div>
      <div className="progress mt-8"><span style={{ width: `${pct}%`, background: pct >= 100 ? 'var(--amber)' : undefined }} /></div>
      {full && <div className="small muted mt-8">Você usou todas as {label.toLowerCase()} do plano.</div>}
    </div>
  )
}
